
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { GuestFeedbackDateTimeTable } from "./GuestFeedbackDateTimeTable";
import { cn } from "@/lib/utils";
import { useEventFetch } from "@/hooks/event-details/use-event-fetch";

interface GuestFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  guestName: string;
  phoneNumber?: string;
  rsvpStatus?: string;
  suggestedVenue?: string | null;
  suggestedTime?: string | null;
  selectedDates?: string[] | null;
  selectedTimes?: string[] | null;
  notes?: string | null;
}

export const GuestFeedbackDialog = ({
  isOpen,
  onClose,
  eventId,
  guestName,
  phoneNumber,
  rsvpStatus,
  suggestedVenue,
  suggestedTime,
  selectedDates,
  selectedTimes,
  notes
}: GuestFeedbackDialogProps) => {
  const hasFeedback = suggestedVenue || suggestedTime || (selectedDates && selectedDates.length > 0) || notes;
  
  // Fetch event details to get all dates
  const { data: eventData } = useEventFetch(eventId);
  const allEventDates = eventData?.preferred_dates || [];
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(
        "sm:max-w-md",
        "rounded-lg", // Ensure rounded corners
        "overflow-hidden" // Prevent content from breaking rounded corners
      )}>
        <DialogHeader>
          <DialogTitle>{guestName}'s Feedback</DialogTitle>
          <DialogDescription>
            {phoneNumber && <span className="block text-sm">Phone: {phoneNumber}</span>}
            {rsvpStatus && (
              <Badge className="mt-1" variant={rsvpStatus === 'accepted' ? 'default' : rsvpStatus === 'declined' ? 'destructive' : 'outline'}>
                {rsvpStatus.charAt(0).toUpperCase() + rsvpStatus.slice(1)}
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="mt-2 max-h-72">
          {hasFeedback ? (
            <div className="space-y-4">
              {suggestedVenue && (
                <div>
                  <h4 className="font-medium text-sm">Suggested Venue</h4>
                  <p className="text-sm mt-1">{suggestedVenue}</p>
                  <Separator className="mt-2" />
                </div>
              )}
              
              {suggestedTime && (
                <div>
                  <h4 className="font-medium text-sm">Suggested Time</h4>
                  <p className="text-sm mt-1">{suggestedTime}</p>
                  <Separator className="mt-2" />
                </div>
              )}
              
              {selectedDates && selectedDates.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm">Available Dates & Times</h4>
                  <GuestFeedbackDateTimeTable 
                    allDates={allEventDates}
                    selectedDates={selectedDates} 
                    selectedTimes={selectedTimes}
                  />
                  <Separator className="mt-2" />
                </div>
              )}
              
              {notes && (
                <div>
                  <h4 className="font-medium text-sm">Additional Notes</h4>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{notes}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No feedback provided.</p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
